import { Button, Modal, Spin } from "antd";
import React, { useCallback, useMemo, useState } from "react";
import { useProject } from "../../../App/state";
import { useFloorplanCalibrationByIdQuery } from "../../../generated/types";
import {
  FloorplanCreateInput,
  FloorplanPatchInput,
} from "../hooks/useFloorplan";
import { FloorplanPrefill, ImportFloorplanModal } from "./ImportFloorplanModal";

interface PatchFloorplanModalContainerProps {
  onPatch: (floorplanData: FloorplanPatchInput) => Promise<void>;
  onClose: () => void;
  floorplanId: string;
  spaceId: string;
  spaceName: string;
}

export const PatchFloorplanModalContainer = (
  props: PatchFloorplanModalContainerProps,
) => {
  const { onPatch, onClose, floorplanId, spaceId, spaceName } = props;
  const [uploadInProgress, setUploadInProgress] = useState(false);
  const project = useProject();

  const { data, loading, error, refetch } = useFloorplanCalibrationByIdQuery({
    variables: {
      tenant: project,
      id: floorplanId,
    },
    fetchPolicy: "network-only",
    // So a Retry from the error state re-enters the loading branch below.
    notifyOnNetworkStatusChange: true,
  });

  const uploadFloorplanCallback = useCallback(
    async (floorplan: FloorplanCreateInput) => {
      setUploadInProgress(true);
      try {
        await onPatch({
          ...floorplan,
          id: floorplanId,
        });
        onClose();
        return floorplanId;
      } finally {
        setUploadInProgress(false);
      }
    },
    [floorplanId, onPatch, onClose],
  );

  const current = data?.floorplans[0];

  const prefill: FloorplanPrefill | undefined = useMemo(
    () =>
      current
        ? {
            originX: current.originX,
            originY: current.originY,
            width: current.width,
            height: current.height,
            mmScaleFactor: current.mmScaleFactor,
            scaleStartX: current.scaleStartX,
            scaleStartY: current.scaleStartY,
            scaleEndX: current.scaleEndX,
            scaleEndY: current.scaleEndY,
            scaleDistanceMm: current.scaleDistanceMm,
            bimOriginX: current.bimOriginX,
            bimOriginY: current.bimOriginY,
            bimOriginZ: current.bimOriginZ,
            floorHeight: current.floorHeight,
            angleToTrueNorth: current.angleToTrueNorth,
            bimProjectId: current.bimProjectId,
            // Mapped explicitly to strip __typename, which the mutation input rejects.
            bimFloorModels:
              current.bimFloorModels?.map((model) => ({
                label: model.label,
                bimFloorId: model.bimFloorId,
                isCommon: model.isCommon,
              })) ?? null,
          }
        : undefined,
    [current],
  );

  // Wait for the current calibration so the modal can be seeded with it.
  if (loading) {
    return (
      <Modal visible={true} closable={false} footer={null} onCancel={onClose}>
        <Spin>
          <div style={{ padding: "48px", textAlign: "center" }}>
            Loading current floor plan configuration...
          </div>
        </Spin>
      </Modal>
    );
  }

  // The patch mutation overwrites the BIM columns with whatever is submitted,
  // and this modal only resubmits what the prefill carries — so replacing
  // without the current floor plan loaded would wipe its BIM configuration.
  // Block the flow instead of proceeding blind.
  if (error || !prefill) {
    return (
      <Modal
        visible={true}
        closable={false}
        onCancel={onClose}
        footer={[
          <Button key="close" onClick={onClose}>
            Close
          </Button>,
          <Button
            key="retry"
            type="primary"
            onClick={() => refetch().catch(() => undefined)}
          >
            Retry
          </Button>,
        ]}
      >
        {error
          ? "Could not load the current floor plan configuration. Replacing the floor plan without it would clear its stored calibration and BIM properties, so the update is blocked. Retry, or close and try again later."
          : "The current floor plan could not be found — it may have been deleted by someone else. Close and refresh the floor plan editor."}
      </Modal>
    );
  }

  return (
    <ImportFloorplanModal
      onClose={onClose}
      spaceId={spaceId}
      spaceName={spaceName}
      createFloorplan={uploadFloorplanCallback}
      uploadInProgress={uploadInProgress}
      prefill={prefill}
    />
  );
};
